// src/pages/Client/ClientDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Container, Row, Col, Badge, Button, Nav, Tab, Modal,
    Form, InputGroup, Dropdown, OverlayTrigger, Tooltip, Spinner
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import axios from '../../api/axios';
import {
    FiPackage, FiShoppingBag, FiClipboard, FiRefreshCw,
    FiSearch, FiFilter, FiChevronRight, FiCopy, FiTruck,
    FiCheckCircle, FiAlertCircle, FiMapPin,
    FiUser, FiMail, FiPhone, FiHome, FiCalendar, FiEye,
    FiChevronLeft, FiExternalLink, FiMessageCircle,
    FiAlertTriangle
} from 'react-icons/fi';

// ========== CONSTANTES ==========
const API_URL = process.env.REACT_APP_API_URL || 'https://chineafriquecargo.com';
const DEFAULT_WHATSAPP = process.env.REACT_APP_DEFAULT_WHATSAPP || '';

// ========== COMPOSANTS SKELETON ==========
const StatSkeleton = () => (
    <div className="stat-skeleton">
        <div className="skeleton-icon"></div>
        <div className="skeleton-value"></div>
        <div className="skeleton-label"></div>
    </div>
);

const TableSkeleton = ({ rows = 5 }) => (
    <div className="table-skeleton">
        {[...Array(rows)].map((_, i) => (
            <div key={i} className="skeleton-row">
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
            </div>
        ))}
    </div>
);

const ProductSkeleton = () => (
    <div className="product-skeleton">
        <div className="skeleton-image"></div>
        <div className="skeleton-title"></div>
        <div className="skeleton-price"></div>
    </div>
);

// ========== COMPOSANT PRINCIPAL ==========
const ClientDashboard = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('parcels');
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [parcelsPage, setParcelsPage] = useState(1);

    // États des données
    const [parcels, setParcels] = useState([]);
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [whatsappNumber, setWhatsappNumber] = useState('');

    // États de chargement
    const [parcelsLoading, setParcelsLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [whatsappLoading, setWhatsappLoading] = useState(true);

    const ITEMS_PER_PAGE = 8;
    const dataFetched = useRef(false);

    // ========== FETCH WHATSAPP ==========
    const fetchWhatsapp = useCallback(async () => {
        try {
            // Essayer le cache d'abord
            const cached = localStorage.getItem('cac_whatsapp');
            if (cached) {
                setWhatsappNumber(cached);
                setWhatsappLoading(false);
            }

            // URLs à essayer
            const urls = [
                `${API_URL}/whatsapp/public/`,
                `${API_URL}/whatsapp/`,
                `https://chineafriquecargo.com/whatsapp/public/`,
                `https://chineafriquecargo.com/whatsapp/public/`,
            ];

            let response = null;
            for (const url of urls) {
                try {
                    response = await axios.get(url, { timeout: 5000 });
                    break;
                } catch (err) {
                }
            }

            if (response?.data) {
                let numbers = [];
                if (response.data.results) numbers = response.data.results;
                else if (Array.isArray(response.data)) numbers = response.data;
                else if (response.data.phone_number) numbers = [response.data];

                if (numbers.length > 0) {
                    const defaultNum = numbers.find(n => n.is_default) || numbers[0];
                    const clean = (defaultNum.phone_number || '').replace(/\D/g, '');
                    setWhatsappNumber(clean);
                    localStorage.setItem('cac_whatsapp', clean);
                }
            }
        } catch (err) {
            console.error('WhatsApp error:', err);
            if (DEFAULT_WHATSAPP) setWhatsappNumber(DEFAULT_WHATSAPP);
        } finally {
            setWhatsappLoading(false);
        }
    }, []);

    // ========== FETCH PARCELS ==========
    const fetchParcels = useCallback(async () => {
        try {
            const res = await axios.get('/parcels/');
            const data = res.data?.results || (Array.isArray(res.data) ? res.data : []);
            setParcels(data);
        } catch (err) {
            console.error('Parcels error:', err);
        } finally {
            setParcelsLoading(false);
        }
    }, []);

    // ========== FETCH ORDERS ==========
    const fetchOrders = useCallback(async () => {
        try {
            const res = await axios.get('/store/orders/');
            setOrders(res.data || []);
        } catch (err) {
            console.error('Orders error:', err);
        } finally {
            setOrdersLoading(false);
        }
    }, []);

    // ========== FETCH PRODUCTS ==========
    const fetchProducts = useCallback(async () => {
        try {
            const res = await axios.get('/store/products/');
            const data = res.data?.results || (Array.isArray(res.data) ? res.data : []);
            setProducts(data.filter(p => p.is_available).slice(0, 4));
        } catch (err) {
            console.error('Products error:', err);
        } finally {
            setProductsLoading(false);
        }
    }, []);

    // ========== CHARGEMENT INITIAL UNIQUE ==========
    useEffect(() => {
        if (!dataFetched.current) {
            dataFetched.current = true;
            fetchWhatsapp();
            fetchParcels();
            fetchOrders();
            fetchProducts();
        }
    }, [fetchWhatsapp, fetchParcels, fetchOrders, fetchProducts]);

    // ========== REFRESH ==========
    const handleRefresh = async () => {
        setRefreshing(true);
        setParcelsLoading(true);
        setOrdersLoading(true);
        setProductsLoading(true);

        await Promise.all([
            fetchWhatsapp(),
            fetchParcels(),
            fetchOrders(),
            fetchProducts()
        ]);

        setRefreshing(false);
        toast.success('✅ Données actualisées');
    };

    // ========== STATISTIQUES ==========
    const stats = useMemo(() => {
        return {
            total: parcels.length,
            inProgress: parcels.filter(p => !['delivered', 'incident'].includes(p.current_status)).length,
            delivered: parcels.filter(p => p.current_status === 'delivered').length,
            orders: orders.length
        };
    }, [parcels, orders]);

    // ========== COLIS FILTRÉS ==========
    const filteredParcels = useMemo(() => {
        let filtered = [...parcels];

        if (statusFilter === 'inProgress') {
            filtered = filtered.filter(p => !['delivered', 'incident'].includes(p.current_status));
        } else if (statusFilter !== 'all') {
            filtered = filtered.filter(p => p.current_status === statusFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                p.tracking_number?.toLowerCase().includes(term) ||
                p.description?.toLowerCase().includes(term)
            );
        }

        filtered.sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            const modifier = sortDirection === 'asc' ? 1 : -1;

            if (sortField.includes('_at')) {
                return (new Date(aVal) - new Date(bVal)) * modifier;
            }
            return String(aVal).localeCompare(String(bVal)) * modifier;
        });

        return filtered;
    }, [parcels, statusFilter, searchTerm, sortField, sortDirection]);

    const paginatedParcels = useMemo(() => {
        const start = (parcelsPage - 1) * ITEMS_PER_PAGE;
        return filteredParcels.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredParcels, parcelsPage]);

    const totalPages = Math.ceil(filteredParcels.length / ITEMS_PER_PAGE);

    // ========== UTILITAIRES ==========
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const copyTrackingNumber = (number) => {
        navigator.clipboard.writeText(number);
        toast.success('Numéro copié !');
    };

    const getStatusConfig = (status) => {
        const configs = {
            registered: { label: 'Enregistré', color: '#64748B', bg: '#F1F5F9', icon: FiPackage },
            transit_air: { label: 'Transit aérien', color: '#3B82F6', bg: '#EFF6FF', icon: FiTruck },
            transit_sea: { label: 'Transit maritime', color: '#3B82F6', bg: '#EFF6FF', icon: FiTruck },
            arrived: { label: 'Arrivé', color: '#F59E0B', bg: '#FFFBEB', icon: FiMapPin },
            delivering: { label: 'En livraison', color: '#F97316', bg: '#FFF7ED', icon: FiTruck },
            delivered: { label: 'Livré', color: '#10B981', bg: '#ECFDF5', icon: FiCheckCircle },
            incident: { label: 'Incident', color: '#EF4444', bg: '#FEF2F2', icon: FiAlertCircle }
        };
        return configs[status] || configs.registered;
    };

    const getOrderStatusConfig = (status) => {
        const configs = {
            pending: { label: 'En attente', color: '#F59E0B', bg: '#FFFBEB' },
            validated: { label: 'Validée', color: '#10B981', bg: '#ECFDF5' },
            refused: { label: 'Refusée', color: '#EF4444', bg: '#FEF2F2' },
            shipped: { label: 'Expédiée', color: '#3B82F6', bg: '#EFF6FF' },
            delivered: { label: 'Livrée', color: '#10B981', bg: '#ECFDF5' }
        };
        return configs[status] || { label: status, color: '#64748B', bg: '#F1F5F9' };
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatPrice = (price) => (parseFloat(price) || 0).toLocaleString();

    // ========== RENDU ==========
    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <Container>
                    <div className="header-content">
                        <div className="welcome-wrapper">
                            <div className="avatar">
                                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                            </div>
                            <div className="welcome-text">
                                <h1>Bonjour, {user?.first_name || user?.email?.split('@')[0]} 👋</h1>
                                <p>Bienvenue sur votre espace client CAC</p>
                            </div>
                        </div>
                        <button
                            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            <FiRefreshCw size={20} />
                        </button>
                    </div>
                </Container>
            </div>

            <Container className="dashboard-container">
                {/* Stats */}
                <div className="stats-grid">
                    {parcelsLoading ? (
                        [...Array(4)].map((_, i) => <StatSkeleton key={i} />)
                    ) : (
                        <>
                            <div className="stat-card">
                                <div className="stat-icon-wrapper" style={{ backgroundColor: '#C6282815' }}>
                                    <FiPackage size={24} color="#C62828" />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{stats.total}</span>
                                    <span className="stat-label">Total colis</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon-wrapper" style={{ backgroundColor: '#F59E0B15' }}>
                                    <FiTruck size={24} color="#F59E0B" />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{stats.inProgress}</span>
                                    <span className="stat-label">En cours</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon-wrapper" style={{ backgroundColor: '#10B98115' }}>
                                    <FiCheckCircle size={24} color="#10B981" />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{stats.delivered}</span>
                                    <span className="stat-label">Livrés</span>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon-wrapper" style={{ backgroundColor: '#3B82F615' }}>
                                    <FiShoppingBag size={24} color="#3B82F6" />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{stats.orders}</span>
                                    <span className="stat-label">Commandes</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Tabs */}
                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                    <div className="tabs-wrapper">
                        <Nav className="custom-tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="parcels">
                                    <FiPackage size={18} />
                                    <span>Mes colis</span>
                                    {!parcelsLoading && <Badge className="tab-badge">{filteredParcels.length}</Badge>}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="store">
                                    <FiShoppingBag size={18} />
                                    <span>CAC Store</span>
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="orders">
                                    <FiClipboard size={18} />
                                    <span>Mes commandes</span>
                                    {!ordersLoading && <Badge className="tab-badge">{orders.length}</Badge>}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </div>

                    <Tab.Content className="tab-content-wrapper">
                        {/* Tab Colis */}
                        <Tab.Pane eventKey="parcels">
                            <div className="content-card">
                                <div className="card-toolbar">
                                    <InputGroup className="search-input">
                                        <InputGroup.Text><FiSearch size={16} /></InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher un colis..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="light" size="sm">
                                            <FiFilter size={14} /> Filtres
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setStatusFilter('all')}>Tous</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setStatusFilter('inProgress')}>En cours</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setStatusFilter('delivered')}>Livrés</Dropdown.Item>
                                            <Dropdown.Item onClick={() => setStatusFilter('incident')}>Incidents</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>

                                {parcelsLoading ? (
                                    <TableSkeleton rows={5} />
                                ) : filteredParcels.length === 0 ? (
                                    <div className="empty-state">
                                        <FiPackage size={48} />
                                        <h4>Aucun colis trouvé</h4>
                                        <p>{searchTerm ? 'Aucun résultat' : 'Vos colis apparaîtront ici'}</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-wrapper">
                                            <table className="modern-table">
                                                <thead>
                                                    <tr>
                                                        <th onClick={() => handleSort('tracking_number')} style={{ cursor: 'pointer' }}>
                                                            N° Suivi {sortField === 'tracking_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th>Description</th>
                                                        <th>Statut</th>
                                                        <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                                            Date {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                                                        </th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paginatedParcels.map((parcel) => {
                                                        const status = getStatusConfig(parcel.current_status);
                                                        const StatusIcon = status.icon;
                                                        return (
                                                            <tr key={parcel.id}>
                                                                <td>
                                                                    <div className="tracking-cell">
                                                                        <code>{parcel.tracking_number}</code>
                                                                        <button className="copy-btn" onClick={() => copyTrackingNumber(parcel.tracking_number)}>
                                                                            <FiCopy size={14} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td>{parcel.description?.substring(0, 40) || '-'}{parcel.description?.length > 40 && '...'}</td>
                                                                <td>
                                                                    <span className="status-badge" style={{ backgroundColor: status.bg, color: status.color }}>
                                                                        <StatusIcon size={14} /> {status.label}
                                                                    </span>
                                                                </td>
                                                                <td>{formatDate(parcel.created_at)}</td>
                                                                <td>
                                                                    <Link to={`/tracking/${parcel.tracking_number}`} className="action-link" target="_blank">
                                                                        Suivre <FiExternalLink size={12} />
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="pagination-wrapper">
                                                <button className="page-btn" onClick={() => setParcelsPage(p => Math.max(1, p - 1))} disabled={parcelsPage === 1}>
                                                    <FiChevronLeft />
                                                </button>
                                                <span className="page-info">Page {parcelsPage} sur {totalPages}</span>
                                                <button className="page-btn" onClick={() => setParcelsPage(p => Math.min(totalPages, p + 1))} disabled={parcelsPage === totalPages}>
                                                    <FiChevronRight />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </Tab.Pane>

                        {/* Tab Store */}
                        <Tab.Pane eventKey="store">
                            <div className="content-card">
                                <div className="card-header">
                                    <h3>Produits populaires</h3>
                                    <Link to="/store" className="see-all-link">Voir tout <FiChevronRight size={16} /></Link>
                                </div>

                                {productsLoading ? (
                                    <div className="products-grid">
                                        {[...Array(4)].map((_, i) => <ProductSkeleton key={i} />)}
                                    </div>
                                ) : (
                                    <div className="products-grid">
                                        {products.map(product => (
                                            <motion.div key={product.id} className="product-card" whileHover={{ y: -6 }}>
                                                <div className="product-image">
                                                    {product.main_image_url ? (
                                                        <img src={product.main_image_url} alt={product.name} loading="lazy" />
                                                    ) : (
                                                        <FiPackage size={40} />
                                                    )}
                                                    {product.is_featured && <span className="featured-badge">⭐ Populaire</span>}
                                                </div>
                                                <div className="product-info">
                                                    <h4>{product.name}</h4>
                                                    <p className="product-price">{formatPrice(product.price_fcfa)} FCFA</p>
                                                    <Link to="/store" className="product-link">Voir le produit</Link>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Tab.Pane>

                        {/* Tab Commandes */}
                        <Tab.Pane eventKey="orders">
                            <div className="content-card">
                                <div className="card-header">
                                    <h3>Historique des commandes</h3>
                                </div>

                                {ordersLoading ? (
                                    <TableSkeleton rows={3} />
                                ) : !orders.length ? (
                                    <div className="empty-state">
                                        <FiShoppingBag size={48} />
                                        <h4>Aucune commande</h4>
                                        <p>Parcourez notre catalogue</p>
                                        <Link to="/store" className="btn-primary">Découvrir</Link>
                                    </div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table className="modern-table">
                                            <thead>
                                                <tr>
                                                    <th>Commande</th>
                                                    <th>Date</th>
                                                    <th>Montant</th>
                                                    <th>Statut</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orders.map(order => {
                                                    const status = getOrderStatusConfig(order.status);
                                                    return (
                                                        <tr key={order.id}>
                                                            <td><code>{order.order_number}</code></td>
                                                            <td>{formatDate(order.created_at)}</td>
                                                            <td className="amount">{formatPrice(order.total_amount)} FCFA</td>
                                                            <td>
                                                                <span className="status-badge" style={{ backgroundColor: status.bg, color: status.color }}>
                                                                    {status.label}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button className="action-link" onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}>
                                                                    <FiEye size={14} /> Détails
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>

                {/* Support Banner */}
                <div className="support-banner">
                    <div className="support-content">
                        <div className="support-icon"><FiMessageCircle size={32} /></div>
                        <div className="support-text">
                            <h4>Besoin d'assistance ?</h4>
                            <p>Notre équipe est disponible pour répondre à toutes vos questions</p>
                        </div>
                    </div>
                    <div className="support-actions">
                        {whatsappLoading ? (
                            <button className="support-btn whatsapp" disabled><Spinner animation="border" size="sm" /></button>
                        ) : whatsappNumber ? (
                            <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="support-btn whatsapp">WhatsApp</a>
                        ) : (
                            <button className="support-btn warning" onClick={fetchWhatsapp}><FiAlertTriangle /> Réessayer</button>
                        )}
                        <Link to="/contact" className="support-btn contact">Contact</Link>
                    </div>
                </div>
            </Container>

            {/* Modal */}
            <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} centered className="order-modal">
                <Modal.Header closeButton><Modal.Title>Commande {selectedOrder?.order_number}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <>
                            <div className="modal-info-grid">
                                <div className="info-item"><FiUser /> {selectedOrder.shipping_name}</div>
                                <div className="info-item"><FiMail /> {selectedOrder.shipping_email}</div>
                                <div className="info-item"><FiPhone /> {selectedOrder.shipping_phone}</div>
                                <div className="info-item"><FiHome /> {selectedOrder.shipping_address}</div>
                                <div className="info-item"><FiCalendar /> {formatDate(selectedOrder.created_at)}</div>
                            </div>
                            <div className="modal-products">
                                {selectedOrder.items?.map((item, i) => (
                                    <div key={i} className="modal-product-item">
                                        <span>{item.name} ×{item.quantity}</span>
                                        <span>{formatPrice(item.price)} FCFA</span>
                                    </div>
                                ))}
                            </div>
                            <div className="modal-total">
                                <span>Total</span>
                                <strong>{formatPrice(selectedOrder.total_amount)} FCFA</strong>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <style>{`
                .dashboard { min-height: 100vh; background: #F8FAFC; }
                .dashboard-header { background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%); padding: 32px 0; }
                .header-content { display: flex; justify-content: space-between; align-items: center; }
                .welcome-wrapper { display: flex; align-items: center; gap: 16px; }
                .avatar { width: 56px; height: 56px; background: linear-gradient(135deg, #C62828, #F9A825); border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 600; color: white; text-transform: uppercase; }
                .welcome-text h1 { color: white; font-size: 24px; font-weight: 600; margin: 0 0 4px; }
                .welcome-text p { color: rgba(255,255,255,0.7); margin: 0; font-size: 14px; }
                .refresh-btn { width: 44px; height: 44px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 12px; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; }
                .refresh-btn.spinning svg { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .dashboard-container { padding: 32px 24px; margin-top: -20px; }
                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 32px; }
                .stat-card { background: white; border-radius: 20px; padding: 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
                .stat-icon-wrapper { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; }
                .stat-value { font-size: 28px; font-weight: 700; color: #0F172A; }
                .stat-label { font-size: 13px; color: #64748B; }
                .tabs-wrapper { background: white; border-radius: 16px; padding: 6px; margin-bottom: 24px; display: inline-flex; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
                .custom-tabs { display: flex; gap: 4px; }
                .custom-tabs .nav-link { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 12px; color: #64748B; font-weight: 500; background: transparent; border: none; }
                .custom-tabs .nav-link:hover { color: #C62828; background: rgba(198,40,40,0.05); }
                .custom-tabs .nav-link.active { background: #C62828; color: white; }
                .tab-badge { background: rgba(0,0,0,0.06); color: inherit; padding: 2px 8px; border-radius: 20px; font-size: 11px; }
                .content-card { background: white; border-radius: 24px; padding: 24px; box-shadow: 0 4px 12px rgba(0,0,0,0.04); }
                .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .card-toolbar { display: flex; gap: 12px; margin-bottom: 20px; }
                .search-input { width: 280px; }
                .modern-table { width: 100%; border-collapse: collapse; }
                .modern-table th { text-align: left; padding: 12px 16px; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #64748B; border-bottom: 1px solid #E2E8F0; }
                .modern-table td { padding: 16px; border-bottom: 1px solid #F1F5F9; font-size: 14px; }
                .tracking-cell { display: flex; align-items: center; gap: 8px; }
                .tracking-cell code { font-family: monospace; font-size: 13px; color: #C62828; background: #FEF2F2; padding: 4px 8px; border-radius: 6px; }
                .copy-btn { background: none; border: none; color: #94A3B8; cursor: pointer; padding: 4px; opacity: 0; }
                .modern-table tr:hover .copy-btn { opacity: 1; }
                .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
                .action-link { display: inline-flex; align-items: center; gap: 4px; color: #C62828; text-decoration: none; font-weight: 500; }
                .products-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
                .product-card { background: white; border-radius: 16px; overflow: hidden; border: 1px solid #F1F5F9; }
                .product-image { height: 160px; background: #F8FAFC; display: flex; align-items: center; justify-content: center; position: relative; }
                .product-image img { max-width: 80%; max-height: 120px; object-fit: contain; }
                .featured-badge { position: absolute; top: 10px; right: 10px; background: #F9A825; color: #0F172A; font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 20px; }
                .product-info { padding: 16px; text-align: center; }
                .product-price { font-size: 16px; font-weight: 700; color: #C62828; margin-bottom: 12px; }
                .product-link { display: block; padding: 8px; background: #F8FAFC; border-radius: 8px; color: #0F172A; text-decoration: none; }
                .product-link:hover { background: #C62828; color: white; }
                .empty-state { text-align: center; padding: 48px; color: #94A3B8; }
                .btn-primary { background: #C62828; border: none; padding: 10px 24px; border-radius: 40px; color: white; text-decoration: none; display: inline-block; margin-top: 16px; }
                .pagination-wrapper { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 24px; padding-top: 16px; border-top: 1px solid #F1F5F9; }
                .page-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid #E2E8F0; background: white; cursor: pointer; }
                .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .support-banner { background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%); border-radius: 20px; padding: 24px 32px; display: flex; align-items: center; justify-content: space-between; margin-top: 32px; }
                .support-icon { width: 60px; height: 60px; background: white; border-radius: 16px; display: flex; align-items: center; justify-content: center; color: #C62828; }
                .support-actions { display: flex; gap: 12px; }
                .support-btn { padding: 12px 24px; border-radius: 40px; font-weight: 500; text-decoration: none; border: none; cursor: pointer; }
                .support-btn.whatsapp { background: #25D366; color: white; }
                .support-btn.contact { background: #C62828; color: white; }
                .support-btn.warning { background: #F59E0B; color: white; }
                
                /* Skeletons */
                .stat-skeleton { background: white; border-radius: 20px; padding: 20px; }
                .skeleton-icon { width: 52px; height: 52px; border-radius: 14px; background: #E2E8F0; margin-bottom: 12px; }
                .skeleton-value { height: 28px; width: 60%; background: #E2E8F0; border-radius: 6px; margin-bottom: 6px; }
                .skeleton-label { height: 14px; width: 40%; background: #E2E8F0; border-radius: 4px; }
                .table-skeleton .skeleton-row { display: flex; gap: 16px; padding: 16px; }
                .table-skeleton .skeleton-cell { flex: 1; height: 20px; background: #E2E8F0; border-radius: 4px; }
                .product-skeleton .skeleton-image { height: 160px; background: #E2E8F0; }
                .product-skeleton .skeleton-title { height: 16px; width: 80%; margin: 16px auto 8px; background: #E2E8F0; }
                .product-skeleton .skeleton-price { height: 20px; width: 50%; margin: 0 auto 16px; background: #E2E8F0; }
                
                /* Modal */
                .modal-info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 20px; }
                .info-item { display: flex; align-items: center; gap: 8px; color: #64748B; }
                .modal-products { background: #F8FAFC; border-radius: 16px; padding: 16px; margin-bottom: 16px; }
                .modal-product-item { display: flex; justify-content: space-between; padding: 8px 0; }
                .modal-total { display: flex; justify-content: space-between; font-size: 18px; font-weight: 600; padding-top: 16px; border-top: 1px solid #E2E8F0; }
                .modal-total strong { color: #C62828; }

                @media (max-width: 992px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } .products-grid { grid-template-columns: repeat(2, 1fr); } }
                @media (max-width: 768px) { .header-content { flex-direction: column; align-items: flex-start; gap: 16px; } .custom-tabs .nav-link span:not(.tab-badge) { display: none; } .card-toolbar { flex-direction: column; } .search-input { width: 100%; } .support-banner { flex-direction: column; text-align: center; gap: 20px; } }
                @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr; } .products-grid { grid-template-columns: 1fr; } }
            `}</style>
        </div>
    );
};

export default ClientDashboard;